import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Input, InputNumber, Form, Space, notification, Divider, Table, Tooltip, Tabs } from 'antd';
import { ColumnsType } from 'antd/es/table';

import Employees from '@components/users/employees';

import PhoneInputFormItem from '@controls/phone-input-form-item/phone-input-form-item';
import FormHeader from '@controls/form-header/form-header';

import { exception } from '@extensions/notification';
import { serverFetch } from '@flc/server';

import { userLoaded } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { IConsignee } from '@entities/consignee';
import { IUserSession } from '@entities/user-session';
import { IUser } from '@entities/user';

import { UserType } from '@enums/user-type';
import { WarehouseType, enumLabel as warehouseTypeLabel } from '@enums/warehouse-type';

interface IAccountParams {
    userId: string | undefined;
    newPassword: string | undefined;
}

const User = () => {
    const { TextArea } = Input;

    const navigate = useNavigate();

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [api, contextHolder] = notification.useNotification();

    const [entity, setEntity] = useState<IUser>();
    const [loading, setLoading] = useState<boolean>(false);
    const [allowSetPassword, setAllowSetPassword] = useState(false);

    useEffect(() => {
        setLoading(true);

        serverFetch(`users/account`, { method: 'GET' })
            .then((data) => {
                setEntity(data);
                setLoading(false);
            })
            .catch((ex) => {
                exception(api, 'Ошибка получения учетной записи', ex, () => d(userLoaded(undefined)));
                setLoading(false);
            });
    }, []);

    const onFinishSave = () => {
        if (!entity) return;

        setLoading(true);

        const account: IAccountParams = { userId: entity.id, newPassword: entity.newPassword };
        serverFetch(`users/account`, { method: 'PUT', bodyData: account })
            .then(() => {
                setLoading(false);
                navigate(-1);
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка сохранения учетной записи', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderUserForm = () => {
        return (
            entity && (
                <Form colon={false} labelCol={{ span: 2 }} wrapperCol={{ span: 8 }} onFinish={onFinishSave}>
                    <Form.Item initialValue={entity?.login} label='Логин' name='code'>
                        <Input disabled={true} />
                    </Form.Item>
                    <Form.Item label='Пароль' rules={[{ required: true, message: 'Укажите пароль' }]}>
                        <Space.Compact style={{ width: '100%' }}>
                            <Input.Password
                                value={entity.newPassword}
                                disabled={!allowSetPassword}
                                placeholder={!allowSetPassword ? '*******' : ''}
                                onChange={(data) => {
                                    setEntity({ ...entity, newPassword: data.target.value });
                                }}
                            />
                            <Button
                                disabled={entity.isDemo}
                                type='primary'
                                onClick={() => {
                                    setAllowSetPassword(true);
                                }}
                            >
                                Сменить пароль
                            </Button>
                        </Space.Compact>
                    </Form.Item>
                    <Form.Item initialValue={entity?.fullName} label='ФИО' name='fullName'>
                        <Input disabled={true} />
                    </Form.Item>
                    <PhoneInputFormItem value={entity?.phone} name='phone' label='Телефон' disabled={true} />
                    <Form.Item initialValue={entity?.email} label='Email' name='email'>
                        <Input disabled={true} />
                    </Form.Item>

                    {entity.newPassword && (
                        <Form.Item wrapperCol={{ offset: 2, span: 8 }}>
                            <Space size={'small'} style={{ float: 'right', marginTop: 0 }}>
                                <Button
                                    variant='link'
                                    color='blue'
                                    onClick={() => {
                                        setEntity({ ...entity, newPassword: undefined });
                                        setAllowSetPassword(false);
                                    }}
                                >
                                    Отменить
                                </Button>
                                <Button type='primary' htmlType='submit' loading={loading}>
                                    Сохранить
                                </Button>
                            </Space>
                        </Form.Item>
                    )}
                </Form>
            )
        );
    };

    const renderConsigneesTable = () => {
        const columns: ColumnsType<IConsignee> = [
            {
                title: 'ID',
                align: 'center',
                dataIndex: 'code',
                width: 70,
            },
            {
                title: 'Маркировка',
                width: 300,
                dataIndex: 'allMarkings',
            },
            {
                title: 'Город доставки',
                width: 150,
                dataIndex: 'cityName',
            },
            {
                title: 'Склад',
                align: 'center',
                width: 70,
                render: (_, record) => {
                    return (
                        <Tooltip title={`${warehouseTypeLabel(record.warehouseType)} склад`}>
                            <div className={record.warehouseType === WarehouseType.Main ? 'mainWarehouseTag' : 'transitWarehouseTag'}>
                                {record.warehouseCode}
                            </div>
                        </Tooltip>
                    );
                },
            },
            {
                title: 'Телефон',
                dataIndex: 'phone',
                width: 170,
                render: (_, record) => {
                    return <>{record.phone}</>;
                },
            },
            {
                title: 'ИНН',
                dataIndex: 'inn',
                width: 110,
            },
            {
                title: 'Контрагент',
                dataIndex: 'legalName',
                width: 200,
            },
            {
                title: 'Юр. адрес',
                dataIndex: 'legalAddress',
                width: 300,
            },
            {
                title: 'Адрес разгрузки',
                dataIndex: 'siteAddress',
                width: 300,
            },
            {
                title: 'Адрес',
                dataIndex: 'address',
                width: 350,
            },
            {
                title: 'ФИО',
                width: 250,
                dataIndex: 'fullName',
            },
        ];

        return (
            <Table
                rowKey='id'
                size='small'
                columns={columns}
                dataSource={entity?.consignees}
                pagination={false}
                scroll={{ y: `calc(100vh - 330px)` }}
            />
        );
    };

    const renderConsignees = () => {
        if (userSession.type == UserType.Supplier) {
            return (
                <>
                    <Divider titlePlacement='left'>Грузополучатели</Divider>
                    {renderConsigneesTable()}
                </>
            );
        }

        return (
            entity &&
            entity.consignees?.map((c) => {
                return (
                    <div key={c.id}>
                        <Divider titlePlacement='left'>Грузополучатель</Divider>

                        <Form key={c.id} colon={false} labelCol={{ span: 2 }} wrapperCol={{ span: 10 }} onFinish={onFinishSave}>
                            <Form.Item initialValue={c.code} label='ID' name='code'>
                                <InputNumber disabled={true} />
                            </Form.Item>
                            <Form.Item initialValue={c.allMarkings} label='Маркировка' name='allMarkings'>
                                <TextArea disabled={true} rows={3} />
                            </Form.Item>
                            <Form.Item initialValue={c.cityName} label='Город доставки' name='city'>
                                <Input disabled={true} />
                            </Form.Item>
                            <Form.Item initialValue={c.fullName} required label='ФИО' name='fullName'>
                                <Input disabled={true} />
                            </Form.Item>
                            <PhoneInputFormItem value={c?.phone} name='phone' label='Телефон' disabled={true} />
                            <Form.Item initialValue={c.cityName} label='Город' name='cityName'>
                                <Input disabled={true} />
                            </Form.Item>
                            <Form.Item initialValue={c.address} label='Адрес' name='address'>
                                <Input disabled={true} />
                            </Form.Item>
                        </Form>
                    </div>
                );
            })
        );
    };

    const tabItems = [
        {
            label: `Основное`,
            key: 'main',
            children: renderUserForm(),
        },
    ];

    if (entity?.id) {
        tabItems.push({
            label: `Сотрудники`,
            key: 'employees',
            children: (
                <Employees userId={entity.id} userLogin={entity.login} userType={entity.type} isViewOnly={true} serverFetch={serverFetch} />
            ),
        });
    }

    return (
        <>
            <FormHeader title={`Личный кабинет`} />
            {!loading && entity && <Tabs size='large' items={tabItems} />}

            {contextHolder}
        </>
    );
};

export default User;
